import { type FormEvent, useState } from 'react';
import { AUTH_TEXT } from '../terms';
import { Button, ErrorText, Field, Section, TextInput } from '../ui';
import { checkSession, login, logout } from './useSession';

/**
 * 로그인 화면 (51회차).
 *
 * 머리의 제목과 같은 자리에 "로그감시"를 두고, 그 아래 상자형 구획 하나에 아이디·비밀번호·단추를
 * 담는다. 색·밀도·말투는 다른 화면과 같다(`docs/console.md`). 따로 꾸미지 않는다.
 *
 * - 비밀번호 칸은 `type="password"`, 자동완성은 `username`·`current-password`로 준다.
 *   브라우저 비밀번호 관리자가 이 둘을 보고 채운다
 * - 실패 문구는 서버가 준 한글을 그대로 쓴다. 틀리면 비밀번호 칸만 비운다
 * - `notice`는 서버에 못 닿았을 때의 까닭이다. 로그인을 시도하면 그 결과 문구로 바뀐다
 */
export function LoginPage({
  notice,
  logoutFailed = false,
}: {
  notice?: string;
  /** 서버에 로그아웃을 못 알렸다. `다시 로그아웃` 단추를 띄운다 */
  logoutFailed?: boolean;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    const message = await login(username, password);
    // 성공하면 이 화면이 내려간다. 내려간 뒤에는 상태를 건드리지 않는다
    if (message === undefined) return;
    setBusy(false);
    setError(message);
    setPassword('');
  };

  const shown = error ?? notice;

  return (
    <main className="min-h-screen bg-canvas px-4 py-6 text-ink sm:px-8">
      <div className="mx-auto mt-16 max-w-sm">
        <h1 className="mb-6 text-2xl font-semibold tracking-tight">로그감시</h1>
        <Section boxed title={AUTH_TEXT.title} titleId="login-title" note={AUTH_TEXT.note}>
          <form className="space-y-3" onSubmit={(event) => void submit(event)}>
            <Field label={AUTH_TEXT.username} htmlFor="login-username">
              <TextInput
                id="login-username"
                name="username"
                autoComplete="username"
                value={username}
                onChange={setUsername}
              />
            </Field>
            <Field label={AUTH_TEXT.password} htmlFor="login-password">
              <TextInput
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
              />
            </Field>
            {shown !== undefined ? <ErrorText>{shown}</ErrorText> : null}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={busy || username === '' || password === ''}
              >
                {busy ? `${AUTH_TEXT.submitting}…` : AUTH_TEXT.submit}
              </Button>
              {/* 서버에 로그아웃을 못 알렸을 때. 쿠키가 남아 있을 수 있다 (리뷰 R51-C-1) */}
              {logoutFailed ? (
                <Button onClick={() => void logout()} disabled={busy}>
                  {AUTH_TEXT.relogout}
                </Button>
              ) : null}
              {/* 첫 확인에서 서버에 못 닿았을 때만. 쿠키가 살아 있으면 비밀번호 없이 들어간다 (리뷰 R51-B) */}
              {notice !== undefined && !logoutFailed ? (
                <Button onClick={() => void checkSession()} disabled={busy}>
                  {AUTH_TEXT.recheck}
                </Button>
              ) : null}
            </div>
          </form>
        </Section>
      </div>
    </main>
  );
}
